import React, { FC } from 'react';

import {
  AlertCategory,
  OperationStatus,
  ReportFor,
} from '@ariksa/notification';
import { Severity } from '@ariksa/notification/api';
import { Status } from '@ariksa/reporting';
import { Box, HStack, Stack, UseDisclosureProps } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { split } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import { DownloadIcon, ExceptionIcon, IssuesIcon } from 'components/Icons';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { selectAlerts } from 'containers/Findings/Alerts/selectors';
import { actions } from 'containers/Findings/Alerts/slice';
import { AlertFilterFields } from 'containers/Findings/Alerts/types';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useSearchParams } from 'hooks/useSearchParams';
import { saveAsFile } from 'utils/download';

interface Props {
  excludeResources?: UseDisclosureProps;
  selectedResources?: string[];
}

export const AlertsTableHeader: FC<Props> = props => {
  const { excludeResources, selectedResources } = props;
  const dispatch = useDispatch();
  const { setLearnMore } = useKnowledgeBase();

  const { aiParamExtraction } = useSelector(selectSharedState);
  const params = useSearchParams<AlertFilterFields>();
  const {
    groupByResource,
    aggregatedFindings,
    alerts,
    filters,
    download,
  } = useSelector(selectAlerts);
  const { environmentId, accountId } = useAccessBoundary();

  const onClickDownload = () => {
    dispatch(
      actions.downloadReport({
        q: {
          environmentId,
          accountId,
          reportType: ReportFor.IndividualAlerts,
          alertCategories: params?.alertCategory
            ? (split(params?.alertCategory, ',') as AlertCategory[])
            : undefined,
          blueprintIds: !!params.blueprintId
            ? ([params.blueprintId] as string[])
            : undefined,
          resourceTypes: params?.resourceType
            ? split(params?.resourceType, ',')
            : undefined,
          severities: params?.severity
            ? (split(params?.severity, ',') as Severity[])
            : undefined,
          entityUuid: params.entity_uuid ?? undefined,
          status:
            params?.alertStatus === 'Open'
              ? Status.Active
              : params?.alertStatus === 'Resolved' ||
                params?.alertStatus === 'Exception'
              ? Status.Resolved
              : undefined,
          region: params?.region ?? undefined,
          search: filters.searchString ?? undefined,
          startDate: !!filters.startTime
            ? dayjs(filters.startTime).utc().format('YYYY-MM-DD')
            : undefined,
          endDate: dayjs(filters.endTime).utc().format('YYYY-MM-DD'),
          operationStatus:
            params?.alertStatus === 'Snoozed'
              ? OperationStatus.Snoozed
              : undefined,
        },
        onSuccess: res => {
          saveAsFile(
            res,
            'Ariksa - Issues Report ' +
              dayjs().format('DD-MMM-YYYY h.mm A') +
              '.csv',
            'text/csv;charset=utf-8;',
          );
        },
      }),
    );
  };

  return (
    <Stack spacing={3} w="full">
      <HStack justify="space-between">
        <Box>
          <PageHeaderWithIcon label="Issues" icon={<IssuesIcon />} />
        </Box>
        <HStack>
          <HStack spacing={0}>
            <LearnMoreButton
              onOpen={() => setLearnMore('issues')}
              label="Issues"
            />
            <OutlinedIconButton
              aria-label="Exclude Resources"
              tooltip={
                groupByResource
                  ? 'Choose one or more resources to enable'
                  : 'View by Resource to enable'
              }
              isDisabled={
                aggregatedFindings.isLoading ||
                alerts.isLoading ||
                aiParamExtraction.isLoading ||
                !groupByResource ||
                !selectedResources?.length
              }
              tooltipStyles={{ tooltip: { w: 32, textAlign: 'center' } }}
              iconSize={6}
              leftIcon={<ExceptionIcon />}
              onClick={() => excludeResources?.onOpen?.()}
            >
              Exclude Resources
            </OutlinedIconButton>
            {!groupByResource && (
              <OutlinedIconButton
                aria-label="Download issues"
                tooltip="Download issues in CSV"
                isDisabled={
                  aggregatedFindings.isLoading ||
                  alerts.isLoading ||
                  aiParamExtraction.isLoading
                }
                iconSize={6}
                leftIcon={<DownloadIcon />}
                onClick={onClickDownload}
                isLoading={download.isLoading}
                loadingText="Downloading..."
              >
                Download In CSV
              </OutlinedIconButton>
            )}
          </HStack>
        </HStack>
      </HStack>
      <LearnMore />
    </Stack>
  );
};
